import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";

/**
 * Cookie consent categories.
 * - necessary: always on (auth, security, load balancing). Not user-toggleable.
 * - analytics: Google Analytics, web vitals, funnel tracking.
 * - preferences: remembered UI choices (theme, layout).
 */
export type ConsentCategory = "necessary" | "analytics" | "preferences";

export type ConsentState = {
  necessary: true;
  analytics: boolean;
  preferences: boolean;
  /** ISO timestamp of the decision. */
  updatedAt: string;
  /** Bump when categories/copy change to re-prompt users. */
  version: number;
};

const STORAGE_KEY = "mr.cookie.consent.v1";
const CONSENT_VERSION = 2;
const OPEN_PREFS_EVENT = "mr:open-cookie-preferences";
const CHANGE_EVENT = "mr:cookie-consent-changed";

export const DEFAULT_DENIED: ConsentState = {
  necessary: true,
  analytics: false,
  preferences: false,
  updatedAt: "",
  version: CONSENT_VERSION,
};

const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function readCookie(): ConsentState | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.split("; ").find((r) => r.startsWith(STORAGE_KEY + "="));
  if (!match) return null;
  try {
    const parsed = JSON.parse(decodeURIComponent(match.slice(STORAGE_KEY.length + 1))) as ConsentState;
    if (parsed?.version !== CONSENT_VERSION) return null;
    return { ...parsed, necessary: true };
  } catch {
    return null;
  }
}

function writeCookie(state: ConsentState) {
  if (typeof document === "undefined") return;
  try {
    const value = encodeURIComponent(JSON.stringify(state));
    document.cookie = `${STORAGE_KEY}=${value}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax`;
  } catch {
    // ignore
  }
}

function readStored(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ConsentState;
      if (parsed?.version === CONSENT_VERSION) return { ...parsed, necessary: true };
    }
  } catch {
    // fall through to cookie
  }
  // Fallback: some browsers (Safari ITP, private mode, embedded previews) clear
  // localStorage between visits. A cookie mirror keeps the decision sticky.
  const fromCookie = readCookie();
  if (fromCookie) {
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(fromCookie)); } catch { /* ignore */ }
    return fromCookie;
  }
  return null;
}

function writeStored(state: ConsentState) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore quota / privacy-mode failures
  }
  writeCookie(state);
}

/** Fire from anywhere (e.g. a footer link) to open the preference center. */
export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_PREFS_EVENT));
}

type ConsentContextValue = {
  consent: ConsentState | null;
  hasDecided: boolean;
  setConsent: (next: Partial<Omit<ConsentState, "necessary" | "updatedAt" | "version">>) => void;
  acceptAll: () => void;
  rejectAll: () => void;
  openPreferences: () => void;
};

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [consent, setConsentState] = useState<ConsentState | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [prefsOpen, setPrefsOpen] = useState(false);

  useEffect(() => {
    setConsentState(readStored());
    setHydrated(true);
    const onOpen = () => setPrefsOpen(true);
    window.addEventListener(OPEN_PREFS_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PREFS_EVENT, onOpen);
  }, []);

  const persist = useCallback((next: ConsentState) => {
    writeStored(next);
    setConsentState(next);
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
  }, []);

  const setConsent: ConsentContextValue["setConsent"] = useCallback(
    (partial) => {
      const next: ConsentState = {
        necessary: true,
        analytics: partial.analytics ?? consent?.analytics ?? false,
        preferences: partial.preferences ?? consent?.preferences ?? false,
        updatedAt: new Date().toISOString(),
        version: CONSENT_VERSION,
      };
      persist(next);
    },
    [consent, persist]
  );

  const acceptAll = useCallback(() => {
    persist({
      necessary: true,
      analytics: true,
      preferences: true,
      updatedAt: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
  }, [persist]);

  const rejectAll = useCallback(() => {
    persist({
      necessary: true,
      analytics: false,
      preferences: false,
      updatedAt: new Date().toISOString(),
      version: CONSENT_VERSION,
    });
  }, [persist]);

  const openPreferences = useCallback(() => setPrefsOpen(true), []);

  const value: ConsentContextValue = {
    consent,
    hasDecided: hydrated && consent !== null,
    setConsent,
    acceptAll,
    rejectAll,
    openPreferences,
  };

  return (
    <ConsentContext.Provider value={value}>
      {children}
      {hydrated ? (
        <CookieConsentUI prefsOpen={prefsOpen} setPrefsOpen={setPrefsOpen} />
      ) : null}
    </ConsentContext.Provider>
  );
}

export function useCookieConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used within CookieConsentProvider");
  return ctx;
}

/** Subscribe to consent changes from non-React code (e.g. analytics loader). */
export function onConsentChange(cb: (state: ConsentState) => void) {
  const handler = (e: Event) => cb((e as CustomEvent<ConsentState>).detail);
  window.addEventListener(CHANGE_EVENT, handler);
  return () => window.removeEventListener(CHANGE_EVENT, handler);
}

export function getStoredConsent(): ConsentState | null {
  return readStored();
}

// ---------- UI ----------

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Link } from "@tanstack/react-router";

function CookieConsentUI({
  prefsOpen,
  setPrefsOpen,
}: {
  prefsOpen: boolean;
  setPrefsOpen: (v: boolean) => void;
}) {
  const { consent, acceptAll, rejectAll, setConsent } = useCookieConsent();
  const showBanner = consent === null && !prefsOpen;

  // Draft toggles for the preference dialog
  const [draftAnalytics, setDraftAnalytics] = useState(consent?.analytics ?? false);
  const [draftPrefs, setDraftPrefs] = useState(consent?.preferences ?? false);

  useEffect(() => {
    if (prefsOpen) {
      setDraftAnalytics(consent?.analytics ?? false);
      setDraftPrefs(consent?.preferences ?? false);
    }
  }, [prefsOpen, consent]);

  return (
    <>
      {showBanner ? (
        <div
          role="dialog"
          aria-live="polite"
          aria-label="Cookie consent"
          className="fixed inset-x-0 bottom-0 z-[100] p-3 sm:p-4"
        >
          <div className="mx-auto max-w-4xl rounded-lg border border-border bg-background/95 p-4 shadow-lg backdrop-blur">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="text-sm text-foreground">
                <p className="font-semibold">Your privacy choices</p>
                <p className="mt-1 text-muted-foreground">
                  Strictly necessary cookies keep sign-in and security working. Analytics and
                  preference cookies are optional and off until you allow them. Location and
                  voice recording inside the app are separate — they are only captured when a
                  signed-in user takes a specific work action and acknowledges the location
                  disclosure. Details in our{" "}
                  <Link to="/privacy-policy" className="underline underline-offset-2">
                    Privacy Policy
                  </Link>
                  .
                </p>
              </div>
              <div className="flex flex-wrap gap-2 sm:flex-nowrap sm:justify-end">
                <Button variant="outline" size="sm" onClick={() => setPrefsOpen(true)}>
                  Preferences
                </Button>
                <Button variant="outline" size="sm" onClick={rejectAll}>
                  Reject all
                </Button>
                <Button size="sm" onClick={acceptAll}>
                  Accept all
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <Dialog open={prefsOpen} onOpenChange={setPrefsOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Choose which cookies MissionReady may use. You can change this any time from the
              footer.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <PrefRow
              title="Strictly necessary"
              description="Required for authentication, security, and core functionality. Always on."
              checked
              disabled
            />
            <PrefRow
              title="Analytics"
              description="Google Analytics and product-usage metrics that help us improve the app. Anonymous where possible."
              checked={draftAnalytics}
              onChange={setDraftAnalytics}
            />
            <PrefRow
              title="Preferences"
              description="Remember UI choices such as saved views and layout. No advertising use."
              checked={draftPrefs}
              onChange={setDraftPrefs}
            />
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button variant="outline" onClick={rejectAll}>
              Reject all
            </Button>
            <Button variant="outline" onClick={acceptAll}>
              Accept all
            </Button>
            <Button
              onClick={() => {
                setConsent({ analytics: draftAnalytics, preferences: draftPrefs });
                setPrefsOpen(false);
              }}
            >
              Save preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function PrefRow({
  title,
  description,
  checked,
  disabled,
  onChange,
}: {
  title: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border p-3">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-foreground">{title}</p>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch
        checked={checked}
        disabled={disabled}
        onCheckedChange={(v) => onChange?.(Boolean(v))}
        aria-label={title}
      />
    </div>
  );
}
